'use client';

import { FC, useContext, useEffect, useState } from 'react';

import { PortalUsersUtils } from '@ravago/shared/next-data-access/client';
import { PortalUser } from '@ravago/shared/page-data/models/elements';
import { Avatar } from '@ravago/shared/radiance/components/avatar/components/Avatar/Avatar';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { Button } from '@ravago/shared/radiance/components/button/components/Button/Button';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Dialog } from '@ravago/shared/radiance/components/dialog/components/Dialog/Dialog';
import { Image } from '@ravago/shared/radiance/components/image/components/Image/Image';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { Table } from '@ravago/shared/radiance/components/table/components/Table/Table';
import { TableBody } from '@ravago/shared/radiance/components/table/components/TableBody/TableBody';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableHead } from '@ravago/shared/radiance/components/table/components/TableHead/TableHead';
import { TableHeader } from '@ravago/shared/radiance/components/table/components/TableHeader/TableHeader';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { usePortalUsers } from '../../../../hooks/use-portal-users';
import { RibbonContext } from '../../../../providers/ribbon-context-providers';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';

interface Props {
   initialPortalUsers: PortalUser[];
}

export const PortalUsersTable: FC<Props> = ({ initialPortalUsers }) => {
   const translate = useTranslations();
   const { portalUsers, setPortalUsers } = usePortalUsers();

   const { dispatch } = useContext(RibbonContext);

   const [loading, setLoading] = useState<boolean>(true);
   const [userToDelete, setUserToDelete] = useState<PortalUser | null>(null);

   const handleDeleteUser = async () => {
      if (!userToDelete) return;

      const { userId, firstName, lastName, status } = userToDelete;

      setUserToDelete(null);

      const previousUsers = [...portalUsers];
      const newUsers = portalUsers.filter((user) => user.userId !== userId);

      setPortalUsers(newUsers);

      if (status === 'DEPROVISIONED') {
         const deleteRequest = await PortalUsersUtils.deletePortalUser(userId);

         if (deleteRequest.success) return;
      } else {
         const deactivateRequest = await PortalUsersUtils.deactivatePortalUser(userId);

         if (deactivateRequest.success) {
            const deleteSuccess = await PortalUsersUtils.deletePortalUser(userId);

            if (deleteSuccess.success) return;
         }
      }

      // Revert to previous state if deletion fails
      setPortalUsers(previousUsers);

      dispatch({
         type: 'show_ribbon',
         value: {
            variant: 'error',
            message: translate('portal-users.delete-failed-description', { username: `${firstName} ${lastName}` }),
            closable: true
         }
      });
   };

   useEffect(() => {
      if (!portalUsers?.length) {
         setPortalUsers(initialPortalUsers);
         setLoading(false);
      }
   }, [initialPortalUsers]);

   if (loading) {
      return (
         <Box direction="row" justify="center" spacing={{ top: '3xl', bottom: '3xl' }}>
            <Spinner />
         </Box>
      );
   }

   if (portalUsers.length === 0 && !loading) {
      return (
         <EmptyState
            image={
               <Image
                  src="/illustrations/empty-state-users.svg"
                  alt={translate('portal-users.no-users-found')}
                  title={translate('portal-users.no-users-found')}
               />
            }
            content={
               <>
                  <Typography variant="heading-2" component="h2" color="strong">
                     {translate('portal-users.no-users-found')}
                  </Typography>
                  <Typography variant="body">{translate('portal-users.no-users-found-description')}</Typography>
               </>
            }
         />
      );
   }

   const renderTableBodyContent = () =>
      portalUsers.map((user, index, arr) => (
         <TableRow key={user.userId} divider={index !== arr.length - 1}>
            <TableCell>
               <Box direction="row" items="center" spacing={{ top: 'xs' }}>
                  <Avatar size="extra-small" shadow={false} variant="account" title={`${user.firstName} ${user.lastName}`} />
                  <Box direction="column" gap="3xs">
                     <Typography variant="body">{`${user.firstName} ${user.lastName}`}</Typography>
                     <Typography variant="small" color="weak">
                        {user.email}
                     </Typography>
                  </Box>
               </Box>
            </TableCell>
            <TableCell>
               <Typography>{translate(`portal-users.statuses.${user.status.toLowerCase()}`)}</Typography>
            </TableCell>
            <TableCell align="right">
               <IconButton variant="danger" icon="trash" onClick={() => setUserToDelete(user)} />
            </TableCell>
         </TableRow>
      ));

   return (
      <Box spacing={{ y: 'md' }} shadow borderRadius="lg" overflowY="auto" background="#FFF" width="100%">
         {userToDelete && (
            <Dialog
               open
               title={translate('portal-users.delete-dialog-title', { username: `${userToDelete.firstName} ${userToDelete.lastName}` })}
               actions={
                  <>
                     <Button variant="secondary" onClick={() => setUserToDelete(null)}>
                        {translate('portal-users.delete-dialog-cancel')}
                     </Button>
                     <Button variant="error" leadingIcon="trash" onClick={handleDeleteUser}>
                        {translate('portal-users.delete-dialog-delete')}
                     </Button>
                  </>
               }
               onDialogClose={() => setUserToDelete(null)}
            >
               <Box direction="column" gap="sm" maxWidth="560px" minWidth="280px">
                  <Typography variant="body">{translate('portal-users.delete-dialog-description-line-1')}</Typography>
                  <Typography variant="body">{translate('portal-users.delete-dialog-description-line-2')}</Typography>
               </Box>
            </Dialog>
         )}

         <Table rowDividers={false} width="100%">
            <TableHead>
               <TableRow divider>
                  <TableHeader>
                     <Box spacing={{ bottom: 'sm' }}>{translate('portal-users.member')}</Box>
                  </TableHeader>
                  <TableHeader>
                     <Box spacing={{ bottom: 'sm' }}>{translate('portal-users.status')}</Box>
                  </TableHeader>
                  <TableHeader align="right">
                     <Box spacing={{ bottom: 'sm' }} justify="end">
                        {translate('portal-users.actions')}
                     </Box>
                  </TableHeader>
               </TableRow>
            </TableHead>
            <TableBody>{renderTableBodyContent()}</TableBody>
         </Table>
      </Box>
   );
};
